import { Link } from '@inertiajs/react'
import { Lock } from 'lucide-react'
import Navbar from '@/components/layout/Navbar'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import type { EpisodeDetail } from '@/types'

const DIFF_COLORS: Record<string, string> = {
  beginner: 'text-green-600',
  intermediate: 'text-orange-500',
  advanced: 'text-red-500',
}

interface Props {
  episode: EpisodeDetail
}

export default function Locked({ episode }: Props) {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        {/* Thumbnail with lock overlay */}
        <div className="relative rounded-2xl overflow-hidden mb-8 mx-auto max-w-xl bg-gray-200 aspect-video">
          {episode.thumbnail_url ? (
            <img
              src={episode.thumbnail_url}
              alt={episode.title}
              className="w-full h-full object-cover opacity-40"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 opacity-60" />
          )}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
              <Lock className="w-8 h-8 text-gray-700" />
            </div>
          </div>
        </div>

        {/* Meta */}
        <p className="text-sm text-gray-400 mb-3">
          <span className={cn('capitalize font-medium', DIFF_COLORS[episode.difficulty])}>
            {episode.difficulty}
          </span>
          <span> • {episode.duration}</span>
          {episode.tags.map(t => (
            <span key={t.slug}> • {t.name}</span>
          ))}
        </p>

        <h1 className="text-3xl font-bold text-gray-900 mb-4">{episode.title}</h1>

        {episode.description && (
          <p className="text-gray-500 mb-8 max-w-xl mx-auto">{episode.description}</p>
        )}

        {/* CTA */}
        <div className="bg-white border border-gray-100 rounded-2xl p-8 max-w-md mx-auto shadow-sm">
          <div className="w-12 h-12 bg-brand/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-5 h-5 text-brand" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Pro episode</h2>
          <p className="text-sm text-gray-500 mb-6">
            Unlock this episode and the full Pro library with a GetCasts Pro subscription.
          </p>
          <div className="flex flex-col gap-3">
            <Link href="/pricing">
              <Button className="w-full">Start Pro — $9/month</Button>
            </Link>
            <Link href="/episodes">
              <Button variant="secondary" className="w-full">Browse free episodes</Button>
            </Link>
          </div>
          <p className="text-xs text-gray-400 mt-4">Cancel anytime. No questions asked.</p>
        </div>
      </div>
    </div>
  )
}
